import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Badge, 
  Menu, 
  MenuItem, 
  Box,
  Avatar,
  Tooltip,
  Divider,
  ListItemIcon,
  alpha,
  useTheme as useMuiTheme
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useTheme } from '../theme/ThemeProvider';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { useNavigate } from 'react-router-dom';
import NotificationsDropdown from '../components/NotificationsDropdown';

const TopBar = ({ open }) => {
  const { currentUser, logout, userRole } = useAuth();
  const { unreadCount } = useNotifications();
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  
  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };
  

  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Get user role display name
  const getRoleDisplayName = (role) => {
    const roleMap = {
      developer: 'Super Admin',
      admin: 'Admin',
      moderator: 'Moderator',
      teacher: 'Teacher'
    };
    return roleMap[role] || role;
  };
  
  // Get user's display name
  const getUserDisplayName = () => {
    if (!currentUser) return 'User';
    return currentUser.displayName || currentUser.email.split('@')[0];
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        width: `calc(100% - ${open ? 240 : 70}px)`,
        ml: `${open ? 240 : 70}px`,
        backgroundColor: darkMode ? muiTheme.palette.background.paper : 'white',
        color: darkMode ? 'white' : 'black',
        boxShadow: darkMode 
          ? '0px 2px 8px rgba(0, 0, 0, 0.2)' 
          : '0px 2px 4px rgba(0, 0, 0, 0.05)',
        transition: 'width 0.3s ease, margin-left 0.3s ease, background-color 0.3s ease',
        borderBottom: `1px solid ${darkMode 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'rgba(0, 0, 0, 0.05)'}`
      }}
    >
      <Toolbar sx={{ 
        py: 1,
        backgroundImage: darkMode 
          ? 'linear-gradient(to right, rgba(0, 0, 0, 0.1), transparent)' 
          : 'linear-gradient(to right, rgba(255, 255, 255, 0.1), transparent)',
      }}>
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            '&::after': {
              content: '""',
              display: 'block',
              width: '30%',
              height: '2px',
              background: `linear-gradient(to right, ${muiTheme.palette.primary.main}, transparent)`,
              marginLeft: 2
            }
          }}
        >
          Admin Dashboard
        </Typography>
        
        {/* User Info & Role Badge */}
        {userRole && (
          <Box 
            sx={{ 
              backgroundColor: darkMode 
                ? alpha(muiTheme.palette.primary.main, 0.2) 
                : alpha(muiTheme.palette.primary.main, 0.1), 
              borderRadius: '16px', 
              padding: '4px 12px',
              marginRight: 2,
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.2)}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'medium',
                color: darkMode 
                  ? muiTheme.palette.primary.light
                  : muiTheme.palette.primary.dark
              }}
            >
              {getRoleDisplayName(userRole)}: {getUserDisplayName()}
            </Typography>
          </Box>
        )}
        
        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton 
            color="inherit" 
            onClick={handleNotificationsClick}
            sx={{ 
              marginRight: 1,
              backgroundColor: alpha(muiTheme.palette.secondary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(muiTheme.palette.secondary.main, 0.2),
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Badge 
              badgeContent={unreadCount} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  boxShadow: '0 0 0 2px ' + muiTheme.palette.background.paper
                }
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <NotificationsDropdown 
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleNotificationsClose}
        />
        
        {/* Profile */}
        <Tooltip title="Profile">
          <IconButton 
            color="inherit" 
            onClick={handleProfileClick}
            sx={{
              p: 0.5,
              border: `2px solid ${alpha(muiTheme.palette.secondary.main, 0.3)}`,
              '&:hover': {
                backgroundColor: alpha(muiTheme.palette.secondary.main, 0.1),
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: muiTheme.palette.primary.main,
                fontWeight: 'bold'
              }}
            >
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileClose}
          PaperProps={{
            elevation: 3,
            sx: { 
              minWidth: 200,
              borderRadius: 2,
              mt: 1.5
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {getUserDisplayName()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentUser?.email}
            </Typography>
          </Box>
          <Divider />
          {/** Get current path for highlighting */}
          {(() => {
            const path = window.location.pathname;
            const isDashboard = path === '/dashboard';
            const isProfile = path === '/settings/user';
            return <>
              <MenuItem onClick={() => {
                handleProfileClose();
                navigate('/dashboard');
              }}>
                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                  <DashboardIcon fontSize="small" sx={{ color: isDashboard ? muiTheme.palette.primary.main : muiTheme.palette.text.primary }} />
                </ListItemIcon>
                <Typography component="span" sx={{ color: isDashboard ? muiTheme.palette.primary.main : muiTheme.palette.text.primary, fontWeight: 500, fontSize: 16 }}>
                  Dashboard
                </Typography>
              </MenuItem>
              <MenuItem onClick={() => {
                handleProfileClose();
                navigate('/settings/user');
              }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" sx={{ color: isProfile ? muiTheme.palette.primary.main : muiTheme.palette.text.primary }} />
                </ListItemIcon>
                <Typography component="span" sx={{ color: isProfile ? muiTheme.palette.primary.main : muiTheme.palette.text.primary, fontWeight: 500, fontSize: 16 }}>
                  My Profile
                </Typography>
              </MenuItem>
            </>;
          })()}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
